/**
 * BannerSection - Station banner management section
 * Wide image (3:1 aspect ratio), max 5MB
 */

import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
  Chip,
  Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ImageIcon from '@mui/icons-material/Image';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { ImageSectionProps, IMAGE_TYPE_CONFIGS, CropModalState, INITIAL_CROP_STATE } from './types';
import ImageUploadDropzone from './ImageUploadDropzone';
import ImageCropModal from './ImageCropModal';

const BannerSection: React.FC<ImageSectionProps> = ({
  stationId,
  currentImage,
  pendingImage,
  rejectedImage,
  uploadingImage,
  onUpload,
  onDelete,
  disabled = false,
  readOnly = false,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [cropModal, setCropModal] = useState<CropModalState>(INITIAL_CROP_STATE);

  const config = IMAGE_TYPE_CONFIGS.banner;
  const hasImage = !!currentImage || !!uploadingImage;
  const hasPendingImage = pendingImage?.status === 'pending_review';
  const isUploading = uploadingImage?.status === 'uploading' || uploadingImage?.status === 'confirming';
  const uploadProgress = uploadingImage?.progress || 0;

  const imageSrc = uploadingImage?.previewUrl || currentImage?.url;

  const handleFileSelect = async (files: File[]) => {
    if (files.length > 0 && !disabled && !readOnly) {
      setImageError(false);
      const file = files[0];
      const previewUrl = URL.createObjectURL(file);
      setCropModal({
        open: true,
        file,
        previewUrl,
      });
    }
  };

  const handleCropComplete = async (processedFile: File) => {
    // Clean up the object URL
    if (cropModal.previewUrl) {
      URL.revokeObjectURL(cropModal.previewUrl);
    }
    setCropModal(INITIAL_CROP_STATE);
    await onUpload(processedFile);
  };

  const handleCropCancel = () => {
    // Clean up the object URL
    if (cropModal.previewUrl) {
      URL.revokeObjectURL(cropModal.previewUrl);
    }
    setCropModal(INITIAL_CROP_STATE);
  };

  const handleReplaceClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileSelect(Array.from(files));
    }
    event.target.value = '';
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (currentImage) {
      await onDelete(currentImage.id);
    }
    setShowDeleteConfirm(false);
  };

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
            <Typography variant="subtitle1" fontWeight={600}>
              {config.label}
            </Typography>
            {hasPendingImage && (
              <Chip
                icon={<HourglassEmptyIcon />}
                label="Pending Review"
                size="small"
                color="warning"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {config.description}
          </Typography>
        </Box>

        {/* Actions */}
        {hasImage && !readOnly && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<CloudUploadIcon />}
              onClick={handleReplaceClick}
              disabled={disabled || isUploading}
            >
              Replace
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              disabled={disabled || isUploading}
            >
              Delete
            </Button>
          </Stack>
        )}
      </Stack>

      {/* Pending Review Alert */}
      {hasPendingImage && !readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          A new banner is awaiting admin approval. The current banner will remain active until the new one is approved.
        </Alert>
      )}

      {/* Rejected Image Alert */}
      {rejectedImage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Banner image was rejected
          </Typography>
          <Typography variant="body2">
            <strong>Reason:</strong> {rejectedImage.rejection_reason || 'No reason provided'}
          </Typography>
          {rejectedImage.rejected_by && (
            <Typography variant="body2">
              <strong>Reviewed by:</strong> {rejectedImage.rejected_by}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Please upload a new banner image.
          </Typography>
        </Alert>
      )}

      {/* Banner Preview */}
      <Box
        sx={{
          width: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
          bgcolor: alpha(theme.palette.grey[500], 0.04),
          border: hasImage ? 'none' : `2px dashed ${theme.palette.divider}`,
        }}
      >
        {hasImage ? (
          <>
            {/* Banner Image */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '3 / 1',
                cursor: currentImage && !isUploading ? 'pointer' : 'default',
              }}
              onClick={() => currentImage && !isUploading && setShowPreview(true)}
            >
              {!imageError ? (
                <Box
                  component="img"
                  src={imageSrc}
                  alt="Station banner"
                  onError={() => setImageError(true)}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: isUploading ? 'brightness(0.6)' : 'none',
                    transition: 'filter 0.2s',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.grey[500], 0.08),
                  }}
                >
                  <ImageIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                </Box>
              )}

              {/* Upload Progress Overlay */}
              {isUploading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.common.black, 0.4),
                  }}
                >
                  {/* Progress Bar */}
                  <Box
                    sx={{
                      width: '60%',
                      maxWidth: 300,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.common.white, 0.3),
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${uploadProgress}%`,
                        height: '100%',
                        bgcolor: 'white',
                        borderRadius: 4,
                        transition: 'width 0.3s ease-out',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: 'white', mt: 1.5, fontWeight: 500 }}
                  >
                    {uploadingImage?.status === 'confirming'
                      ? 'Processing...'
                      : `Uploading ${uploadProgress}%`}
                  </Typography>
                </Box>
              )}

              {/* Hover Overlay */}
              {currentImage && !isUploading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.common.black, 0.3),
                      opacity: 1,
                    },
                  }}
                >
                  <FullscreenIcon sx={{ color: 'white', fontSize: 48 }} />
                </Box>
              )}
            </Box>

            {/* Error Badge */}
            {uploadingImage?.status === 'error' && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  bgcolor: alpha(theme.palette.error.main, 0.9),
                  color: 'white',
                  p: 1.5,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2">
                  {uploadingImage.error || 'Upload failed. Please try again.'}
                </Typography>
              </Box>
            )}
          </>
        ) : (
          // Empty state with dropzone
          <Box sx={{ p: 3 }}>
            {!readOnly ? (
              <ImageUploadDropzone
                onDrop={handleFileSelect}
                accept={config.acceptTypes}
                maxSize={config.maxSize}
                disabled={disabled}
                label={`Recommended: 1200x400px (3:1 ratio) - Max ${config.maxSize / (1024 * 1024)}MB`}
              />
            ) : (
              <Box
                sx={{
                  aspectRatio: '3 / 1',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography color="text.secondary">No banner image</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Hidden file input for replace */}
      <input
        ref={fileInputRef}
        type="file"
        accept={config.acceptExtensions}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />

      {/* Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Banner Preview</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 2,
            }}
          >
            <Box
              component="img"
              src={currentImage?.url}
              alt="Station banner"
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                borderRadius: 2,
                boxShadow: theme.shadows[4],
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <DialogTitle>Delete Banner?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this banner image? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Crop Modal */}
      {cropModal.open && cropModal.file && cropModal.previewUrl && (
        <ImageCropModal
          open={cropModal.open}
          imageSrc={cropModal.previewUrl}
          imageType="banner"
          originalFile={cropModal.file}
          onComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </Box>
  );
};

export default BannerSection;
